import { Log, LogCategory, Operation as LogOperation } from '@augloop/logging';
import { openDB } from 'idb';
import * as crossFetch from 'cross-fetch';
const fetchResource = (typeof fetch !== 'undefined') ? fetch : crossFetch;
var ResourceReturnType;
(function (ResourceReturnType) {
    ResourceReturnType["URL"] = "URL";
    ResourceReturnType["ArrayBuffer"] = "ArrayBuffer";
})(ResourceReturnType || (ResourceReturnType = {}));
export class ModelDownloader {
    constructor(hostApp, baseURL, polymerBaseURL, options) {
        this.dbName = 'ALModels_db';
        this.osName = 'ALModels_os';
        this.dbVersion = 2;
        /**
         * TODO 6637661 delete logic needed for old params/transition
         * 3 options from constructor:
         * 1. hostApp, baseURL (old params)
         * 2. hostApp, baseURL, polymerBaseURL, options (transition where some models in both places)
         * 3. polymerBaseURL, options (new params)
         */
        this.isPolymerSupported = Boolean(polymerBaseURL);
        this.baseURL = `https://${baseURL}/web/${hostApp}`;
        this.polymerBaseURL = this.isPolymerSupported ? `https://${polymerBaseURL}` : '';
        this.checkIdbByFeatures = options === null || options === void 0 ? void 0 : options.checkIdbByFeatures;
        // Start log operation
        const updateMetadataOp = new LogOperation({
            operationName: 'CreateModelDownloader',
            success: true,
            resultDescription: ''
        });
        updateMetadataOp.start();
        this.isBrowserIdbCompatible().then((canUseIdb) => {
            if (!canUseIdb) {
                updateMetadataOp.resultDescription = 'Incompatible browser for IDB';
                Log.info(0x201c0142 /* tag_6hafc */, LogCategory.CoreDefault, updateMetadataOp.stop());
            }
            else {
                let timesUpdated = 0;
                let timesDeleted = 0;
                let timesCursorIncreased = 0;
                let id;
                const timeoutPromise = new Promise((_resolve, reject) => {
                    const ms = 10000;
                    id = setTimeout(() => {
                        reject(new Error(`Took longer than ${ms} ms`));
                    }, ms);
                });
                const updateTimesSinceUsagePromise = this.accessDB().then((db) => {
                    const transaction = db.transaction(this.osName, 'readwrite');
                    return transaction.objectStore(this.osName).openCursor().then(function updateTimes(cursor) {
                        timesCursorIncreased++;
                        if (!cursor) {
                            return transaction.done;
                        }
                        else {
                            const model = cursor.value;
                            if (!model.timesSinceUsage) {
                                model.timesSinceUsage = 0;
                            }
                            model.timesSinceUsage++;
                            let actionPromise;
                            if (model.timesSinceUsage > 5) {
                                // Delete unused models
                                timesDeleted++;
                                actionPromise = cursor.delete();
                            }
                            else {
                                // Update number of sessions since model was used
                                timesUpdated++;
                                actionPromise = cursor.update(model);
                            }
                            // Wait for current action to be complete before moving to next item in db
                            return actionPromise.then(() => {
                                return cursor.continue();
                            }).then(updateTimes);
                        }
                    });
                });
                this.initPromise = Promise.race([timeoutPromise, updateTimesSinceUsagePromise]).catch((err) => {
                    updateMetadataOp.success = false;
                    updateMetadataOp.resultDescription += `${err.message} `;
                }).then(() => {
                    updateMetadataOp.dimension0 = `${timesCursorIncreased}`;
                    updateMetadataOp.dimension1 = `${timesUpdated}`;
                    updateMetadataOp.dimension2 = `${timesDeleted}`;
                    clearTimeout(id);
                    this.closeDBAndEndLog(updateMetadataOp, `initializing idb storage`);
                });
            }
        }).catch(() => {
            updateMetadataOp.resultDescription = 'Issue determining idb compatibility';
            Log.info(0x20017718 /* tag_6ax2y */, LogCategory.CoreDefault, updateMetadataOp.stop());
        });
    }
    getResourceAsArrayBuffer(modelId, version, modelFilters) {
        // Start log operation
        const getModelABOp = new LogOperation({
            operationName: 'GetResource',
            success: true,
            resultDescription: '',
            dimension0: ResourceReturnType.ArrayBuffer,
            dimension1: modelId,
            dimension2: version,
        });
        getModelABOp.start();
        const dict = {};
        return this.getResource(modelId, version, modelFilters, ResourceReturnType.ArrayBuffer, getModelABOp, (model) => {
            return Promise.all(Array.from(model.keys()).map((key) => {
                const value = model.get(key);
                if (value.arrayBuffer) {
                    return value.arrayBuffer().then((abValue) => {
                        dict[key] = abValue;
                    });
                }
                else {
                    return new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.readAsArrayBuffer(value);
                        reader.onload = (event) => {
                            const abVal = event.target.result;
                            if (!(abVal instanceof ArrayBuffer)) {
                                reject(new Error('Unable to obtain ArrayBuffer'));
                            }
                            else {
                                dict[key] = abVal;
                                resolve();
                            }
                        };
                    });
                }
            }));
        }).then(() => {
            return dict;
        });
    }
    getResourceAsURL(modelId, version, modelFilters) {
        // Start log operation
        const getModelURLOp = new LogOperation({
            operationName: 'GetResource',
            success: true,
            resultDescription: '',
            dimension0: ResourceReturnType.URL,
            dimension1: modelId,
            dimension2: version
        });
        getModelURLOp.start();
        const dict = {};
        return this.getResource(modelId, version, modelFilters, ResourceReturnType.URL, getModelURLOp, (model) => {
            return Promise.all(Array.from(model.keys()).map((key) => {
                // Translate blob to resourcePath
                dict[key] = URL.createObjectURL(model.get(key));
            }));
        }).then(() => {
            return dict;
        });
    }
    getResource(modelId, version, modelFilters, type, logOp, cb) {
        return this.getFiles(modelId, version, modelFilters, logOp)
            .then((model) => {
            return cb(model);
        }).then(() => {
            this.closeDBAndEndLog(logOp, `retrieving ${modelId}`);
        }).catch((err) => {
            if (logOp.resultDescription.indexOf(err.message) >= 0) {
                logOp.success = false;
                logOp.resultDescription += `${err.message} `;
            }
            logOp.resultSignature = `Issue retrieving from CDN or translating to ${type}`;
            this.closeDBAndEndLog(logOp, `retrieving ${modelId}`);
            throw err;
        });
    }
    // Checks for idb compatibility given this bug https://bugzilla.mozilla.org/show_bug.cgi?id=1246615
    checkFirefoxCompatibility(firefoxIndex) {
        // Get version number for Firefox - 'Firefox/{versionNum}'
        const version = globalThis.navigator.userAgent.substring(firefoxIndex + 8, firefoxIndex + 11);
        version.replace('.', '');
        // Error only occurs in older versions, so only need to check below v70
        if (parseInt(version, 10) <= 70) {
            return new Promise(function (resolve, reject) {
                // eslint-disable-next-line security/detect-non-literal-fs-filename
                const openRequest = globalThis.indexedDB.open(this.dbName, this.dbVersion);
                openRequest.onsuccess = function () {
                    resolve(true);
                };
                openRequest.onerror = function (error) {
                    reject(error);
                    return true;
                };
            });
        }
        else {
            return Promise.resolve(true);
        }
    }
    isBrowserIdbCompatible() {
        var _a, _b;
        if (typeof Array === 'undefined' || typeof Array.from === 'undefined' || typeof Proxy === 'undefined'
            || typeof URL === 'undefined' || typeof URL.createObjectURL === 'undefined') {
            return Promise.resolve(false);
        }
        else if (typeof globalThis !== 'undefined' && ((_a = globalThis.navigator) === null || _a === void 0 ? void 0 : _a.userAgent)) {
            if (globalThis.navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
                return globalThis.indexedDB ? this.checkFirefoxCompatibility(globalThis.navigator.userAgent.toLowerCase().indexOf('firefox')) : Promise.resolve(false);
            }
            else if (globalThis.navigator.userAgent.toLowerCase().indexOf('safari') > -1
                && globalThis.navigator.userAgent.toLowerCase().indexOf('chrome') <= -1) {
                if (this.checkIdbByFeatures) {
                    // Safari will not allow use of idb unless specific criteria are met
                    /**
                     * Must check browser is Safari before checking requestStorageAccess
                     * because some idb compatible browsers do not support requestStorageAccess (ex. chrome)
                     * but there are some Safari browsers that also do not support requestStorageAccess
                     * so without checking if browser is Safari, there's no way to know whether to resolve true or false
                     * based on if requestStorageAccess is supported
                     * */
                    if ((_b = globalThis.document) === null || _b === void 0 ? void 0 : _b.hasStorageAccess) {
                        return globalThis.document.hasStorageAccess().then(() => {
                            return true;
                        }).catch(() => {
                            return false;
                        });
                    }
                    else {
                        return Promise.resolve(false);
                    }
                }
                else {
                    return Promise.resolve(false);
                }
            }
            else {
                return Promise.resolve(true);
            }
        }
        else {
            return Promise.resolve(false);
        }
    }
    accessDB() {
        if (!this.accessDBPromise) {
            return this.callOpenDB();
        }
        else {
            return this.accessDBPromise.then((db) => {
                //eslint-disable-next-line no-unused-expressions
                return db.transaction(this.osName).done;
            }).then(() => {
                return this.accessDBPromise;
            }).catch(() => {
                return this.callOpenDB();
            });
        }
    }
    callOpenDB() {
        return this.accessDBPromise = openDB(this.dbName, this.dbVersion, {
            upgrade: (db) => {
                if (db.objectStoreNames.contains(this.osName)) {
                    db.deleteObjectStore(this.osName); // Old version has different keyPath, so must delete & rebuild
                }
                db.createObjectStore(this.osName, { keyPath: 'id', autoIncrement: true });
            },
        });
    }
    endLog(logOp, task) {
        if (logOp.success) {
            logOp.resultDescription = `Success ${task}`;
            Log.info(0x222c504f /* tag_8lfbp */, LogCategory.CoreDefault, logOp.stop());
        }
        else {
            logOp.resultDescription += `error ${task}`;
            Log.error(0x222c5050 /* tag_8lfbq */, LogCategory.CoreDefault, logOp.stop());
        }
    }
    closeDBAndEndLog(logOp, task) {
        if (this.accessDBPromise) {
            void this.accessDBPromise.then((db) => {
                db.close();
            }).catch((err) => {
                if (logOp.resultDescription.indexOf(err.message) >= 0) {
                    logOp.success = false;
                    logOp.resultDescription += `${err.message ? err.message : 'Error closing db'} `;
                    logOp.resultSignature = 'Issue closing db';
                }
            }).then(() => {
                this.accessDBPromise = undefined;
                this.endLog(logOp, task);
            });
        }
        else {
            this.endLog(logOp, task);
        }
    }
    /**
     * Retrieves files from IDB or, if that does not work, CDN.
     * If error occurs but files are still retrieved, will record error but still resolve with files.
     * @param modelId
     * @param version
     * @param modelFilters filters (ex language/platform) to get accurate model
     * @param logOp
     *
     * @returns Model from IDB or CDN.
     */
    getFiles(modelId, version, modelFilters, logOp) {
        return this.isBrowserIdbCompatible().catch((err) => {
            logOp.success = false;
            logOp.resultDescription += `${err.message} `;
            logOp.resultSignature = 'Issue determining idb compatibility';
            return false;
        }).then((canUseIdb) => {
            const selectEndptAndDownloadFiles = () => {
                if (modelFilters) {
                    if (this.isPolymerSupported) {
                        return this.downloadFiles1CDN(modelId, version, modelFilters, logOp, canUseIdb);
                    }
                    else {
                        // Unexpected scenario
                        throw new Error('Missing polymerBaseURL provided at host level for queries with modelFilters');
                    }
                }
                else {
                    return this.downloadFiles(modelId, version, logOp, canUseIdb);
                }
            };
            if (!canUseIdb) {
                return selectEndptAndDownloadFiles();
            }
            else {
                return this.getFilesFromIDB(modelId, version, modelFilters, logOp).catch((err) => {
                    if (err) {
                        if (logOp.resultDescription.indexOf(err.message) >= 0) {
                            logOp.success = false;
                            logOp.resultDescription += `${err.message} `;
                        }
                        logOp.resultSignature = 'Issue retrieving from DB';
                    }
                    return selectEndptAndDownloadFiles();
                });
            }
        });
    }
    getFilesFromIDB(modelId, version, modelFilters, logOp) {
        return this.initPromise.catch((err) => {
            logOp.success = false;
            logOp.resultDescription += `${err.message} `;
            logOp.resultSignature = 'Issue initializing';
        }).then(() => {
            return this.getModelOptions(modelId);
        }).then((modelObjs) => {
            for (const modelObj of modelObjs) {
                // Check if correct version of model
                if (modelObj && modelObj.model && modelObj.version == version) {
                    // Continue loop if filters exists but doesn't match request
                    if (!this.checkModelFiltersPass(modelFilters, modelObj.filters)) {
                        continue;
                    }
                    void this.putModel(modelId, modelObj.model, version, modelObj.filters, logOp, modelObj.id); // Update timesSinceUsage
                    return Promise.resolve(modelObj.model);
                }
            }
            return Promise.reject(); // Need to retrieve models from CDN
        });
    }
    checkModelFiltersPass(modelFilters, mapFromModel) {
        var _a;
        for (const keyVal of modelFilters) {
            const modelValuesLowerCase = (_a = mapFromModel.get(keyVal[0])) === null || _a === void 0 ? void 0 : _a.map(element => {
                return element.toLocaleLowerCase();
            });
            if (!modelValuesLowerCase) {
                throw new Error(`Expected filter ${keyVal[0]} to exist in metadata file`);
            }
            else if (modelValuesLowerCase.indexOf(keyVal[1].toLocaleLowerCase()) < 0) {
                return false;
            }
        }
        return true;
    }
    downloadFiles1CDN(modelId, version, modelFilters, logOp, canUseIdb) {
        logOp.dimension3 = 'Polymer'; // dimension3 = CDN files are downloaded from
        let modelInfoMap;
        if (!modelFilters) {
            modelFilters = new Map();
        }
        // Download discovery file
        return fetchResource(`${this.polymerBaseURL}/${modelId}/${version}/metadata.json`).then((response) => {
            if (response.ok) {
                return response.text();
            }
            else {
                throw new Error(`CDN download response status: ${response.status}`);
            }
        }).then((discovery) => {
            var _a, _b;
            const discoveryInfo = JSON.parse(discovery);
            if (discoveryInfo.name !== modelId || discoveryInfo.version !== version) {
                throw new Error(`Expected: ${modelId} version ${version}, received: ${discoveryInfo.name} version ${discoveryInfo.version}`);
            }
            // Use filters to find correct model
            for (const modelInfo of discoveryInfo.variants) {
                modelInfoMap = new Map(Object.entries(modelInfo));
                if (!this.checkModelFiltersPass(modelFilters, modelInfoMap)) { // filters from model don't match what is requested
                    continue;
                }
                if (discoveryInfo.catalog ? modelFilters.size + 2 : modelFilters.size + 1 !== modelInfoMap.size) { // difference from catalog & model_files
                    logOp.success = false;
                    logOp.resultDescription += `Expected ${(_a = modelInfoMap.keys) === null || _a === void 0 ? void 0 : _a.length} # of filters, received ${(_b = modelFilters.keys) === null || _b === void 0 ? void 0 : _b.length}`;
                }
                return modelInfo.model_files;
            }
            throw new Error('No model found with given filters');
        }).then((modelFiles) => {
            // Download each file
            return Promise.all(modelFiles.map(catalogElement => {
                return fetchResource(`${this.polymerBaseURL}/${modelId}/${version}/${catalogElement}`).then((response) => {
                    if (response.ok) {
                        return response.blob();
                    }
                    else {
                        throw new Error(`CDN download response status: ${response.status}`);
                    }
                }).then((blobFile) => {
                    return [catalogElement, blobFile];
                });
            }));
        }).then((arr) => {
            const modelMap = new Map(arr);
            if (canUseIdb) {
                return this.putModel(modelId, modelMap, version, modelInfoMap, logOp); // Add model to IDB
            }
            return modelMap;
        });
    }
    downloadFiles(modelId, version, logOp, canUseIdb) {
        logOp.dimension3 = 'FS'; // dimension3 = CDN files are downloaded from
        // Download catalog
        return fetchResource(`${this.baseURL}/catalog_${modelId}_${version}.json`).then((response) => {
            if (response.ok) {
                return response.text();
            }
            else {
                throw new Error(`CDN download response status: ${response.status}`);
            }
        }).then((catalog) => {
            const catalogInfo = JSON.parse(catalog);
            // URL add-on to resources
            const middleURL = `${catalogInfo.MajorVersion}.${catalogInfo.MinorVersion}/ModelResources`;
            const modelInfo = `${modelId}/${version}/`;
            // Download each file from folder
            return Promise.all(catalogInfo.Resources.map(catalogElement => {
                if (catalogElement.Name.length > modelInfo.length && catalogElement.Name.substring(0, modelInfo.length) === modelInfo) {
                    catalogElement.Name = catalogElement.Name.substring(modelInfo.length);
                }
                return fetchResource(`${this.baseURL}/${middleURL}/${modelInfo}${catalogElement.Name}.${catalogElement.Type}`).then((response) => {
                    if (response.ok) {
                        return response.blob();
                    }
                    else {
                        throw new Error(`CDN download response status: ${response.status}`);
                    }
                }).then((blobFile) => {
                    return [`${catalogElement.Name}.${catalogElement.Type}`, blobFile];
                });
            }));
        }).then((arr) => {
            const modelMap = new Map(arr);
            if (canUseIdb) {
                return this.putModel(modelId, modelMap, version, new Map(), logOp); // Add model to IDB
            }
            return modelMap;
        });
    }
    getModelOptions(modelId) {
        return this.accessDB().then((db) => {
            const modelOptions = [];
            // Iterate through object store and add matching models to modelOptions[]
            const transaction = db.transaction(this.osName, 'readonly');
            return transaction.objectStore(this.osName).openCursor().then(function evaluateModel(cursor) {
                if (!cursor) {
                    return transaction.done;
                }
                else {
                    const model = cursor.value;
                    if (model.modelId == modelId) {
                        modelOptions.push(model);
                    }
                    return cursor.continue().then(evaluateModel);
                }
            }).then(() => {
                return modelOptions;
            });
        });
    }
    // Used to put new models in & update times since usage
    putModel(modelId, model, version, filters, logOp, modelObjKey) {
        if (!filters) {
            filters = new Map();
        }
        return this.accessDB().then((db) => {
            return db.put(this.osName, { modelId: modelId, model: model, version: version, filters: filters, timesSinceUsage: 0 }, modelObjKey);
        }).catch((err) => {
            // Error putting model in IDB
            if (logOp.resultDescription.indexOf(err.message) >= 0) {
                logOp.success = false;
                logOp.resultDescription += `${err.message} `;
            }
            logOp.resultSignature = 'Issue adding to DB';
        }).then(() => {
            return (model);
        });
    }
}
//# sourceMappingURL=model-downloader.js.map